import React, { useEffect, useState } from 'react'

import { MapPin, Printer } from 'lucide-react'
import { useParams } from 'react-router'
import { where } from 'firebase/firestore'
import moment from 'moment'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import html2pdf from 'html2pdf.js'

import { Button } from '../../components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card'
import { Separator } from '../../components/ui/separator'
import ViewsLoader from '../../components/loader/ViewsLoader'
import { ReceiptClassesList } from './components/ReceiptClassesList/ReceiptClassesList'
import { ReceiptFeesList } from './components/ReceiptFeesList/ReceiptFeesList'
import { ReceiptTicketsList } from './components/ReceiptTicketsList/ReceiptTicketsList'
import { ReceiptTotalList } from './components/ReceiptTotalList/ReceiptTotalList'

import FirestoreService from '../../services/firestoreService'

import { getUserFullName } from '../../helpers/helpers'
import { getRegistrationEventDataInDb } from '../../helpers/registration-on-event'
import { getCurrency, getTotalPrices, ITotalPrices } from '../../helpers/payment'
import useToasterHelper from '../../helpers/ToasterHelper'
import { toBase64FromUrl } from '../../commonHelpers/helpers'

import { RecipientModel } from '../../models/recipients/recipients'
import { EventReviewPublishModel } from '../../models/event-review-publish/event-review-publish.model'
import { UserPublicModel } from '../../models/users-public/users-public.model'
import { HorseModel } from '../../models/horse/horse.model'

import { IRecipientInterface } from '../../models/recipients/recipients.interface'
import { IUserPublicInterface } from '../../models/users-public/users-public.interface'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IHorseData } from '../../models/horse/horse.interface'

import { CONST } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { FEES_CATEGORY_CONST } from '../../components/events/views/details/EventDetailsViewComponentFees'
import { currencies } from '../../fakeData/currencies'
const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export type IReceiptData = {
  classes: { items: IRegistrationByDayInterface[]; totalPrice: ITotalPrices }
  fees: { items: IRegistrationFeesInterface[]; totalPrice: ITotalPrices }
  mandatoryFees: { items: IRegistrationFeesInterface[]; totalPrice: ITotalPrices }
  tickets: { items: IRegistrationTicketInterface[]; totalPrice: ITotalPrices }
  totalTax: number
  scratchTax: number
  scratchTotal: number
  refundTotal: number
  paidTotal: number
  unpaidTotal: number
  paidByCash: number
  paidByPaypal: number
  paidByCheck: number
  paidByCard: number
  paidByVenmo: number
}
const ReceiptRootPage = () => {
  const toasterFunctions = useToasterHelper()

  const { registrationId } = useParams<{ registrationId: string }>()

  const [eventOwner, setEventOwner] = useState<IUserPublicInterface | null>(null)
  const [recipient, setRecipient] = useState<IRecipientInterface | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)
  const [allHorses, setAllHorses] = useState<IHorseData[] | null>(null)
  const [receiptData, setReceiptData] = useState<IReceiptData | null>(null)
  const [eventLogo, setEventLogo] = useState('')
  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)

  const handlePrint = async () => {
    setButtonLoading(true)

    const element = document.getElementById('pdf-area')
    if (!element) return

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5], // top, right, bottom, left
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { avoid: '.break-inside-avoid' },
    }

    html2pdf()
      .set(opt)
      .from(element)
      .outputPdf('blob')
      .then((pdfBlob: Blob) => {
        const blobUrl = URL.createObjectURL(pdfBlob)
        window.open(blobUrl, '_blank')
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }

  useEffect(() => {
    const getEventDetails = async () => {
      const allHorses_: IHorseData[] = []
      setLoading(true)

      try {
        const recipientSnapshots = await FirestoreService.filterItems(COLLECTIONS.RECIPIENT.NAME, [
          where(COLLECTIONS.RECIPIENT.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        ])

        if (!recipientSnapshots.size) {
          toasterFunctions.info({ message: MESSAGES_CONST.NO_RECIPIENT })
          return null
        }

        const recipient_ = RecipientModel.fromFirestoreDoc(recipientSnapshots.docs[0]).toObject()

        const publishedEventSnapShot = await FirestoreService.getItem(
          COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
          recipient_?.eventId || ''
        )

        if (!publishedEventSnapShot.exists()) {
          toasterFunctions.info({ message: MESSAGES_CONST.EVENT_DATA_NOT_AVAILABLE })
          return null
        }

        const publishedEvent_ =
          EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()

        const ownerSnapshot = await FirestoreService.getItem(
          COLLECTIONS.USERS_PUBLIC.NAME,
          publishedEvent_.EventDetails?.owner ?? ''
        )
        const eventOwner_ = UserPublicModel.fromFirestoreDoc(ownerSnapshot).toObject()

        const { registrationsByDay, registrationsFees, registrationsTickets } =
          await getRegistrationEventDataInDb({
            registrationId,
            type: ['classes', 'fees', 'tickets'],
          })

        const horseSnaps = await FirestoreService.filterItems(COLLECTIONS.HORSES.NAME)
        horseSnaps.forEach((horse) => {
          const current = HorseModel.fromFirestoreDoc(horse).toObject()
          allHorses_.push(current)
        })

        const classes = registrationsByDay.filter((rider) => !rider.isDivision)

        const fees = registrationsFees.filter(
          (fee) => fee.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE
        )
        const mandatoryFees = registrationsFees.filter(
          (fee) => fee.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE
        )

        // Calculate total paid amount
        const {
          totalRegistrations,
          totalFees,
          totalGovernance,
          totalTickets,
          scratchTotal,
          totalTax,
          scratchTax,
          refundTotal,
          paidTotal,
          unpaidTotal,
          paidByCash,
          paidByPaypal,
          paidByCheck,
          paidByCard,
          paidByVenmo,
        } = getTotalPrices({
          registrationsEntries: classes,
          registrationsFees: registrationsFees,
          registrationsTickets: registrationsTickets,
          isChargeSalesTax: publishedEvent_?.EventPaymentSettings?.chargeSalesTax ?? false,
        })

        setEventOwner(eventOwner_)
        setRecipient(recipient_)
        setPublishedEvent(publishedEvent_)
        setAllHorses(allHorses_)
        setReceiptData({
          classes: { items: classes, totalPrice: totalRegistrations },
          fees: { items: fees, totalPrice: totalFees },
          mandatoryFees: { items: mandatoryFees, totalPrice: totalGovernance },
          tickets: { items: registrationsTickets, totalPrice: totalTickets },
          totalTax,
          scratchTax,
          scratchTotal,
          refundTotal,
          paidTotal,
          unpaidTotal,
          paidByCash,
          paidByPaypal,
          paidByCheck,
          paidByCard,
          paidByVenmo,
        })
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }

    if (registrationId) getEventDetails().then()
  }, [registrationId])

  useEffect(() => {
    if (eventOwner?.userProfilePicture) {
      toBase64FromUrl(eventOwner?.userProfilePicture).then((base64) => setEventLogo(base64))
    }
  }, [eventOwner?.userProfilePicture])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <ViewsLoader
          className="flex items-center w-full justify-center min-h-[30vh] mt-3"
          size="lg"
          color="#F70763"
        />
      </div>
    )
  }

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  return (
    <div className="text-SeabiscuitDark200ThemeColor min-h-screen h-full overflow-auto bg-gray-50 px-4 pb-4 md:px-8 md:pb-8 print:bg-white print:p-0">
      <div className="bg-gray-50 sticky left-0 top-0 pt-4 md:pt-8 pb-4">
        <div className="max-w-2xl mx-auto flex justify-end print:hidden">
          <Button
            disabled={buttonLoading}
            onClick={handlePrint}
            variant="outline"
            className="flex items-center gap-2 border-solid"
          >
            {buttonLoading ? (
              <AutorenewIcon fontSize="small" className="animate-spin h-4 w-4" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            Print Receipt
          </Button>
        </div>
      </div>
      <Card
        id="pdf-area"
        className="max-w-2xl mx-auto w-full bg-white text-gray-900 print:shadow-none print:border-none"
      >
        <CardHeader className="space-y-4 border-b border-black">
          <div className="flex justify-between items-start">
            <div className="flex items-center justify-center w-12 h-12 rounded-full border overflow-hidden uppercase text-sm font-semibold">
              {eventLogo ? (
                <img src={eventLogo} className="w-full h-full object-cover" alt="" />
              ) : (
                <span>
                  {eventOwner
                    ? `${getUserFullName(eventOwner)?.split(' ')?.[0]?.[0]}.${getUserFullName(eventOwner)?.split(' ')?.[1]?.[0]}`
                    : 'N/A'}
                </span>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">
                {eventOwner ? getUserFullName(eventOwner) : 'N/A'}
              </p>
              {recipient?.id && <p className="text-xs text-gray-400">Receipt ID: {recipient.id}</p>}
              {recipient?.modified && (
                <p className="text-xs text-gray-400">
                  {moment(recipient.modified).format('MMMM Do, YYYY')}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <CardTitle className="text-xl">{recipient?.eventName ?? 'N/A'}</CardTitle>
            <p className="text-sm text-gray-500">{recipient?.recipientName ?? 'N/A'}</p>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <MapPin className="w-4 h-4" />
              {publishedEvent?.EventDetails?.competitionCity},{' '}
              {publishedEvent?.EventDetails?.competitionState},{' '}
              {publishedEvent?.EventDetails?.competitionCountry}
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-0">
          <ReceiptClassesList
            allHorses={allHorses}
            classes={receiptData?.classes}
            currency={currency}
          />
          <Separator />
          <ReceiptFeesList fees={receiptData?.fees} currency={currency} />
          <Separator />
          <ReceiptFeesList type="mandatory" fees={receiptData?.mandatoryFees} currency={currency} />
          <Separator />
          <ReceiptTicketsList tickets={receiptData?.tickets} currency={currency} />
          <Separator />
          <ReceiptTotalList
            receiptData={receiptData}
            publishedEvent={publishedEvent}
            currency={currency}
          />
        </CardContent>
      </Card>
    </div>
  )
}
export default ReceiptRootPage
